import _ from 'lodash';
import {
  AccountManagementBeneficiaryModel,
  BeneficiaryAccountType,
  BeneficiaryType,
  RE_SSN,
} from '@tradingblock/types';

const ACCOUNT_MANAGEMENT_BLOCK_SELECTOR = '.block-account-management';

/**
 * Scrolls the Account Management dashboard block to the top of the viewport.
 * Used after beneficiary save so the user sees the block header and tabs.
 */
export function scrollAccountManagementBlockIntoView(): void {
  const el = document.querySelector<HTMLElement>(ACCOUNT_MANAGEMENT_BLOCK_SELECTOR);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else {
    console.warn(
      '[AccountManagement] scrollAccountManagementBlockIntoView: element not found',
      ACCOUNT_MANAGEMENT_BLOCK_SELECTOR
    );
  }
}

/** Human-readable labels for Formik paths under beneficiaries[n].* */
export function humanizeBeneficiaryField(path: string): string {
  if (path.startsWith('address')) {
    return 'Address';
  }
  const segments = path.split('.');
  const top = segments[0] != null ? segments[0] : path;
  const map: Record<string, string> = {
    beneficiaryAccountType: 'Primary or contingent',
    beneficiaryType: 'Individual or trust',
    relationship: 'Relationship',
    firstName: 'First name',
    middleName: 'Middle name',
    lastName: 'Last name',
    suffix: 'Suffix',
    dateOfBirth: 'Date of birth',
    ssnSecret: 'SSN/ITIN',
    trustName: 'Trust name',
    einSecret: 'EIN',
    percent: 'Percent',
  };
  return map[top] || top;
}

/** Groups flat Formik errors into one line per beneficiary index for the summary alert. */
export function summarizeBeneficiaryErrors(errors: Record<string, unknown>): string[] {
  const indexToFields = new Map<number, Set<string>>();
  const re = /^beneficiaries\[(\d+)\]\.(.+)$/;
  for (const key of Object.keys(errors)) {
    const m = key.match(re);
    if (m) {
      const idx = Number(m[1]);
      const rest = m[2];
      const human = humanizeBeneficiaryField(rest);
      if (!indexToFields.has(idx)) {
        indexToFields.set(idx, new Set());
      }
      indexToFields.get(idx)!.add(human);
    }
  }
  return [...indexToFields.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([idx, labels]) => {
      const unique = [...labels].sort();
      return `Beneficiary ${idx + 1}: ${unique.join(', ')}`;
    });
}

const BENE_INDEX_RE = /^beneficiaries\[(\d+)\]/;

/**
 * Scrolls the beneficiary edit card for the first Formik error into view (by data-bene-form-index).
 */
export function scrollToFirstBeneficiaryFormError(errors: Record<string, unknown>): void {
  const keys = Object.keys(errors).filter(k => k.startsWith('beneficiaries['));
  if (keys.length === 0) {
    return;
  }
  keys.sort();
  const m = keys[0].match(BENE_INDEX_RE);
  if (!m) {
    return;
  }
  const idx = m[1];
  const el = document.querySelector(`[data-bene-form-index="${idx}"]`);
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } else {
    console.warn('[Beneficiaries] scrollToFirstBeneficiaryFormError: no element for index', idx);
  }
}

/**
 * True if any Formik error key belongs to beneficiaries[i] (any field, including address.*).
 */
export function beneficiaryIndexHasClientErrors(index: number, errors: Record<string, unknown>): boolean {
  const prefix = `beneficiaries[${index}].`;
  return Object.keys(errors).some(k => k.startsWith(prefix) && errors[k] != null && String(errors[k]) !== '');
}

/**
 * Concise, ordered list of what is blocking a successful save (share rules + per-row hints).
 * Used for the "What's blocking save" strip.
 */
export function buildClientSaveBlockerLines(
  values: { beneficiaries: AccountManagementBeneficiaryModel[] },
  errors: Record<string, unknown>
): string[] {
  const lines: string[] = [];
  const b = values.beneficiaries;
  const primary = b.filter(x => x.beneficiaryAccountType === BeneficiaryAccountType.Primary);
  const cont = b.filter(x => x.beneficiaryAccountType === BeneficiaryAccountType.Contingent);
  const pSum = _.sumBy(primary, x => _.toNumber(x.percent) || 0);
  const cSum = _.sumBy(cont, x => _.toNumber(x.percent) || 0);

  if (primary.length > 0 && pSum !== 100) {
    lines.push(`Primary shares total ${pSum}% (must be 100%).`);
  }
  if (cont.length > 0) {
    if (primary.length > 0 && pSum !== 100) {
      lines.push('Finish primary shares at 100% before contingent shares can be saved.');
    } else if (cSum !== 100) {
      lines.push(`Contingent shares total ${cSum}% (must be 100%).`);
    }
  }

  const perRow = summarizeBeneficiaryErrors(errors);
  perRow.forEach(s => {
    if (lines.length < 8) {
      lines.push(s);
    }
  });

  return lines;
}

/** Step 1: both role steppers have a valid selection. */
export function isBeneficiaryRoleRowComplete(b: AccountManagementBeneficiaryModel): boolean {
  const acct = b.beneficiaryAccountType;
  const t = b.beneficiaryType;
  return (
    (acct === BeneficiaryAccountType.Primary || acct === BeneficiaryAccountType.Contingent) &&
    (t === BeneficiaryType.Individual || t === BeneficiaryType.Trust)
  );
}

/** Step 2a: individual identity fields that gate the Share step (aligns with required field validation). */
export function isIndividualCoreCompleteForProgress(b: AccountManagementBeneficiaryModel): boolean {
  if (b.beneficiaryType !== BeneficiaryType.Individual) {
    return false;
  }
  const dob = b.dateOfBirth;
  const ssnItin = b.ssnSecret != null ? String(b.ssnSecret).trim() : '';
  return Boolean(
    b.firstName &&
      String(b.firstName).trim() &&
      b.lastName &&
      String(b.lastName).trim() &&
      ssnItin &&
      RE_SSN.test(ssnItin) &&
      b.relationship &&
      String(b.relationship).trim() &&
      dob &&
      dob.day != null &&
      dob.month != null &&
      dob.year != null
  );
}

/** Step 2b: trust name + EIN to gate the Share step. */
export function isTrustCoreCompleteForProgress(b: AccountManagementBeneficiaryModel): boolean {
  if (b.beneficiaryType !== BeneficiaryType.Trust) {
    return false;
  }
  return Boolean(
    b.trustName && String(b.trustName).trim() && b.einSecret && String(b.einSecret).trim()
  );
}

/** Step 3: share % valid — gates the Address step. */
export function isShareCompleteForProgress(b: AccountManagementBeneficiaryModel): boolean {
  return b.percent != null && _.isInteger(b.percent) && b.percent >= 1 && b.percent <= 100;
}

export function isShareSectionVisible(b: AccountManagementBeneficiaryModel): boolean {
  if (!isBeneficiaryRoleRowComplete(b)) {
    return false;
  }
  if (b.beneficiaryType === BeneficiaryType.Individual) {
    return isIndividualCoreCompleteForProgress(b);
  }
  if (b.beneficiaryType === BeneficiaryType.Trust) {
    return isTrustCoreCompleteForProgress(b);
  }
  return false;
}

export function isAddressSectionVisible(b: AccountManagementBeneficiaryModel): boolean {
  return isShareSectionVisible(b) && isShareCompleteForProgress(b);
}

/**
 * Scrolls a beneficiary form section into view (smooth). Double rAF + short timeout
 * so newly revealed sections are in the DOM before measurement.
 */
export function scrollBeneFormSectionToView(elementId: string): void {
  if (typeof document === 'undefined' || !elementId) {
    return;
  }
  const run = () => {
    const el = document.getElementById(elementId);
    if (!el) {
      console.warn('[Beneficiaries] scroll target not found', elementId);
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  window.setTimeout(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(run);
    });
  }, 32);
}
